import {
  achievementDataTemplate,
  achievementTippingDataIcons,
  DataType,
  levelColorCodes,
} from "./constants";
import { displayDateFormat, displayDateStringFormat } from "./functions";
import { InvoiceGraphEntry, ApiConnectionDataEntry } from "./interfaces";

export const kpiFormat = (data: any) => {
  const res: any[] = [];
  achievementDataTemplate.forEach((el, idx) => {
    switch (idx) {
      case 0:
        const serviceRate = parseFloat((data.serviceRate * 100).toFixed(2));
        res.push({
          icon: el.icon,
          altText: el.altText,
          title: el.title,
          value: serviceRate,
          unit: el.unit,
        });
        break;
      case 1:
        const totalWaste = data.totalWaste;
        res.push({
          icon: el.icon,
          altText: el.altText,
          title: el.title,
          value: totalWaste,
          unit: el.unit,
        });
        break;
      case 2:
        const recycledWaste = data.recycledWaste;
        res.push({
          icon: el.icon,
          altText: el.altText,
          title: el.title,
          value: recycledWaste,
          unit: el.unit,
        });
        break;
      case 3:
        const wasteForIncineration = data.wasteForIncineration;
        res.push({
          icon: el.icon,
          altText: el.altText,
          title: el.title,
          value: wasteForIncineration,
          unit: el.unit,
        });
        break;
      case 4:
        const recyclingRate = parseFloat((data.recyclingRate * 100).toFixed(1));
        res.push({
          icon: el.icon,
          altText: el.altText,
          title: el.title,
          value: recyclingRate,
          unit: el.unit,
        });
        break;
      case 5:
        const incinerationRate = parseFloat(
          (data.incinerationRate * 100).toFixed(1)
        );
        res.push({
          icon: el.icon,
          altText: el.altText,
          title: el.title,
          value: incinerationRate,
          unit: el.unit,
        });
        break;
      case 6:
        const energyFromWaste = Math.floor(data.energyFromWaste);
        res.push({
          icon: el.icon,
          altText: el.altText,
          title: el.title,
          value: energyFromWaste,
        });
        break;
      case 7:
        const CO2EmissionsGenerated = data.CO2EmissionsGenerated;
        res.push({
          icon: el.icon,
          altText: el.altText,
          title: el.title,
          value: CO2EmissionsGenerated,
          unit: el.unit,
        });
        break;
      case 8:
        const CO2Saved = data.CO2Saved;
        res.push({
          icon: el.icon,
          altText: el.altText,
          title: el.title,
          value: CO2Saved,
          unit: el.unit,
        });
        break;
      case 9:
        const treesSaved = Math.floor(data.treesSaved);
        res.push({
          icon: el.icon,
          altText: el.altText,
          title: el.title,
          value: treesSaved,
        });
    }
  });
  return res;
};

export const wasteTypeChartFormat = (data: any[]) => {
  const tableData: any[] = [];
  const pieData: any[] = [];
  const colorArray = [];
  const filteredData = data.filter(
    (el) => el.dataset === DataType.WASTE_TYPE_PERCENTAGE
  );
  const totalValue = filteredData.reduce(
    (prev, item) => prev + item.totalWeight,
    0
  );
  filteredData.forEach((item) => {
    const parsedItem = [
      item.wasteTypeLabel,
      item.totalWeight.toFixed(3),
      (item.percentage * 100).toFixed(2) + "%",
    ];
    tableData.push(parsedItem);
    if (item.typeColorCode && item.typeColorCode.length > 0) {
      colorArray.push(item.typeColorCode);
      pieData.push({
        name: item.wasteTypeLabel,
        value: item.totalWeight,
        fill: item.typeColorCode,
      });
    } else {
      colorArray.push("#000000");
      pieData.push({
        name: item.wasteTypeLabel,
        value: item.totalWeight,
        fill: "#000000",
      });
    }
  });
  const totalCount = totalValue.toFixed(3);
  colorArray.push("#ffffff");
  return {
    tableData,
    pieData,
    colorArray,
    totalCount,
  };
};

export const printableWasteTypeChartFormat = (data: any[]) => {
  const tableData: any[] = [];
  const pieData: any[] = [];
  const colorArray: string[] = [];
  const fullColorArray: any[] = [];
  const filteredData = data.filter(
    (el) => el.dataset === DataType.WASTE_TYPE_PERCENTAGE_PRINTABLE
  );
  data.forEach((item) => {
    if (item.wasteTypeLabel) {
      const searchIdx = fullColorArray.findIndex(
        (el) => el.label === item.wasteTypeLabel
      );
      if (
        searchIdx < 0 &&
        item.typeColorCode &&
        item.typeColorCode.length > 0
      ) {
        fullColorArray.push({
          label: item.wasteTypeLabel,
          color: item.typeColorCode,
        });
      }
    }
  });
  filteredData.forEach((item) => {
    const parsedItem = [
      item.wasteTypeLabel,
      item.totalWeight.toFixed(3),
      (item.percentage * 100).toFixed(2) + "%",
    ];
    tableData.push(parsedItem);
    const searchIdx = fullColorArray.findIndex(
      (el) => el.label === item.wasteTypeLabel
    );
    if (searchIdx >= 0) {
      colorArray.push(fullColorArray[searchIdx].color);
      pieData.push({
        name: item.wasteTypeLabel,
        value: item.totalWeight,
        fill: fullColorArray[searchIdx].color,
      });
    } else {
      colorArray.push("#000000");
      pieData.push({
        name: item.wasteTypeLabel,
        value: item.totalWeight,
        fill: "#000000",
      });
    }
  });
  return {
    tableData,
    pieData,
    colorArray,
  };
};

export const recycledRecoveredChartFormat = (data: any[]) => {
  const rrDataArray: any[] = [];
  const rrTableArray: any[] = [];
  const rrColorArray: any[] = [];
  const filteredData = data.filter(
    (el) => el.dataset === DataType.WASTE_RECOVERED_RECYCLED
  );
  filteredData.forEach((item) => {
    const displayPeriodLabel = displayDateStringFormat(item.periodLabel);
    rrTableArray.push([
      displayPeriodLabel,
      item.recycledOrRecovered,
      item.totalWeight,
    ]);

    let searchIdx = rrDataArray.findIndex(
      (el) => el.name === displayPeriodLabel
    );
    if (searchIdx >= 0) {
      const temp = rrDataArray.splice(searchIdx, 1);
      if (item.recycledOrRecovered.toLowerCase() === "recycled") {
        rrDataArray.push({
          name: displayPeriodLabel,
          recycled: item.totalWeight,
          recovered: temp[0].recovered,
        });
      } else {
        rrDataArray.push({
          name: displayPeriodLabel,
          recycled: temp[0].recycled,
          recovered: item.totalWeight,
        });
      }
    } else {
      if (item.recycledOrRecovered.toLowerCase() === "recycled") {
        rrDataArray.push({
          name: displayPeriodLabel,
          recycled: item.totalWeight,
          recovered: 0,
        });
      } else {
        rrDataArray.push({
          name: displayPeriodLabel,
          recycled: 0,
          recovered: item.totalWeight,
        });
      }
    }

    searchIdx = rrColorArray.findIndex(
      (el) => el.dataKey === item.recycledOrRecovered.toLowerCase()
    );
    if (searchIdx < 0) {
      rrColorArray.push({
        dataKey: item.recycledOrRecovered.toLowerCase(),
        color: item.rorColorCode,
      });
    }
  });
  return {
    data: rrDataArray,
    table: rrTableArray,
    colors: rrColorArray,
  };
};

export const emissionsChartFormat = (data: any[]) => {
  const filteredData = data.filter(
    (el) => el.dataset === DataType.CO2_EMISSIONS
  );
  return filteredData.map((item) => {
    return {
      name: displayDateStringFormat(item.periodLabel),
      value: parseFloat(item.co2EmissionsGenerated.toFixed(3)),
    };
  });
};

export const wasteTypeByPeriodChartFormat = (data: any[]) => {
  const graphRes: any[] = [];
  const tableRes: any[] = [];
  const types: any[] = [];
  const filteredData = data.filter(
    (el) => el.dataset === DataType.WASTE_TYPE_BY_PERIOD
  );
  filteredData.forEach((item) => {
    tableRes.push([item.periodLabel, item.wasteTypeLabel, item.totalWeight]);

    const searchIdx = graphRes.findIndex((el) => el.name === item.periodLabel);
    let temp: any;
    if (searchIdx >= 0) temp = graphRes.splice(searchIdx, 1)[0];
    else temp = { name: item.periodLabel };
    temp[item.wasteTypeLabel] = item.totalWeight;
    graphRes.push(temp);

    const typesId = types.findIndex((el) => el.label === item.wasteTypeLabel);
    if (typesId < 0)
      types.push({
        label: item.wasteTypeLabel,
        color:
          item.typeColorCode && item.typeColorCode.length > 0
            ? item.typeColorCode
            : "#000000",
      });
  });
  const adjustedGraphArr = graphRes
    .filter((item) => Object.keys(item).length > 1)
    .map((item) => {
      types.forEach((t) => {
        if (!item[t.label]) item[t.label] = 0;
      });
      return item;
    });
  return {
    graphArray: adjustedGraphArr,
    tableArray: tableRes,
    typeArray: types,
  };
};

export const wasteTypeBySiteChartFormat = (data: any[]) => {
  const graphRes: any[] = [];
  const tableRes: any[] = [];
  const types: any[] = [];
  const filteredData = data.filter(
    (el) => el.dataset === DataType.WASTE_TYPE_BY_SITE
  );
  filteredData.forEach((item) => {
    tableRes.push([
      item.customerSiteLabel,
      item.wasteTypeLabel,
      item.totalWeight,
    ]);

    const searchIdx = graphRes.findIndex(
      (el) => el.name === item.customerSiteLabel
    );
    let temp: any;
    if (searchIdx >= 0) temp = graphRes.splice(searchIdx, 1)[0];
    else temp = { name: item.customerSiteLabel };
    temp[item.wasteTypeLabel] = item.totalWeight;
    graphRes.push(temp);

    const typesId = types.findIndex((el) => el.label === item.wasteTypeLabel);
    if (typesId < 0)
      types.push({
        label: item.wasteTypeLabel,
        color:
          item.typeColorCode && item.typeColorCode.length > 0
            ? item.typeColorCode
            : "#000000",
      });
  });
  const adjustedGraphArr = graphRes
    .filter((item) => Object.keys(item).length > 1)
    .map((item) => {
      types.forEach((t) => {
        if (!item[t.label]) item[t.label] = 0;
      });
      return item;
    })
    .sort((a, b) => {
      const aTot: any = Object.values(a).reduce(
        (tot, el: any, idx) => (idx > 0 ? tot + el : tot),
        0
      );
      const bTot: any = Object.values(b).reduce(
        (tot, el: any, idx) => (idx > 0 ? tot + el : tot),
        0
      );
      return bTot - aTot;
    });
  return {
    graphArray: adjustedGraphArr,
    tableArray: tableRes,
    typeArray: types,
  };
};

export const topSitesByRecyclingRateFormat = (data: any[]) => {
  const graphRes: any[] = [];
  const tableRes: any[] = [];
  const filteredData = data.filter(
    (el) => el.dataset === DataType.LB_RECYCLING_RATE_TOP || el.dataset === DataType.LB_RECYCLING_RATE_BOTTOM
  );
  filteredData.forEach((item) => {
    const recycleRatePercentage = parseFloat(
      (item.recyclingRate * 100).toFixed(1)
    );
    tableRes.push([
      item.customerSiteLabel,
      recycleRatePercentage,
      item.totalRecycled,
      item.totalWaste,
    ]);
    graphRes.push({
      name: item.customerSiteLabel,
      value: recycleRatePercentage,
    });
  });
  const adjustedGraphArr = graphRes
    .sort((a, b) => b.value - a.value)
    .filter((_, idx) => idx < 5);
  const adjustedTableArr = tableRes.sort((a, b) => b[1] - a[1]);
  return {
    graphArray: adjustedGraphArr,
    tableArray: adjustedTableArr,
  };
};

export const bottomSitesByEmissionsRateFormat = (data: any[]) => {
  const graphRes: any[] = [];
  const tableRes: any[] = [];
  const filteredData = data.filter(
    (el) => el.dataset === DataType.LB_EMISSIONS_RATE_TOP || el.dataset === DataType.LB_EMISSIONS_RATE_BOTTOM
  );
  filteredData.forEach((item) => {
    const emissionsRatePercentage = parseFloat(
      (item.emissionsRate * 100).toFixed(1)
    );
    const roundedTotalEmissions = (item.totalEmissions ?? 0).toFixed(3);
    tableRes.push([
      item.customerSiteLabel,
      emissionsRatePercentage,
      roundedTotalEmissions,
      item.totalWaste,
    ]);
    graphRes.push({
      name: item.customerSiteLabel,
      value: emissionsRatePercentage,
    });
  });
  const adjustedGraphArr = graphRes
    .sort((a, b) => a.value - b.value)
    .filter((_, idx) => idx < 5);
  const adjustedTableArr = tableRes.sort((a, b) => a[1] - b[1]);
  return {
    graphArray: adjustedGraphArr,
    tableArray: adjustedTableArr,
  };
};

export const topSitesByReducedWasteRateFormat = (data: any[]) => {
  const graphRes: any[] = [];
  const tableRes: any[] = [];
  const filteredData = data.filter(
    (el) => el.dataset === DataType.LB_REDUCED_WASTE_TOP || el.dataset === DataType.LB_REDUCED_WASTE_BOTTOM
  );
  filteredData.forEach((item) => {
    const reducedWasteRatePercentage = parseFloat(
      (item.reducedWasteRate * 100).toFixed(1)
    );
    tableRes.push([
      item.customerSiteLabel,
      reducedWasteRatePercentage,
      item.totalWeightCY,
      item.totalWeightYA,
    ]);
    graphRes.push({
      name: item.customerSiteLabel,
      value: reducedWasteRatePercentage,
    });
  });
  const adjustedGraphArr = graphRes
    .sort((a, b) => b.value - a.value)
    .filter((_, idx) => idx < 5);
  const adjustedTableArr = tableRes.sort((a, b) => b[1] - a[1]);
  return {
    graphArray: adjustedGraphArr,
    tableArray: adjustedTableArr,
  };
};

export const jobListFormat = (data: any[]) => {
  const formatterTime = new Intl.DateTimeFormat("en-UK", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  return data.map((job: any) => {
    let jobDate = "";
    if (job.jobDate) {
      const jobDateNumber = parseInt(job.jobDate, 10);
      jobDate = displayDateFormat(new Date(jobDateNumber));
    }
    let jobArrTime = "";
    if (job.arrTime) {
      const jobArrTimeNumber = parseInt(job.arrTime, 10);
      jobArrTime = formatterTime.format(new Date(jobArrTimeNumber));
    }
    let jobDepTime = "";
    if (job.depTime) {
      const jobDepTimeNumber = parseInt(job.depTime, 10);
      jobDepTime = formatterTime.format(new Date(jobDepTimeNumber));
    }
    return [
      jobDate,
      job.site || "",
      job.jobNumber ? job.jobNumber : -1,
      jobArrTime,
      jobDepTime,
      job.status || "",
      job.service || "",
      job.wasteType || "",
      job.EWCCode || "",
      job.weight ? job.weight : 0,
      job.quantity ? job.quantity : 0,
      job.CO2Emissions ? parseFloat(job.CO2Emissions.toFixed(1)) : 0.0,
      job.downloadJobTicketLink || "",
    ];
  });
};

export const jobTippingListFormat = (data: any[]) => {
  return data.map((job: any) => {
    return [
      job.date ?? "",
      job.vehicleRegNo ?? "",
      job.round ?? "",
      job.ticket ?? "",
      job.arrivalTime ?? "",
      job.departureTime ?? "",
      job.turnaroundTime ?? "",
      job.grossWeight ?? "",
      job.tareWeight ?? "",
      job.netWeight ?? "",
      job.contaminationLevel ?? "",
      job.gateFee ?? "",
      job.totalFee ?? "",
    ];
  });
};

export const issueListFormat = (data: any[]) => {
  return data.map((issue: any) => {
    let issueDate = "";
    if (issue.jobDate) {
      const issueDateNumber = parseInt(issue.jobDate, 10);
      issueDate = displayDateFormat(new Date(issueDateNumber));
    }
    return [
      issueDate,
      issue.site || "",
      issue.jobNumber ? issue.jobNumber : -1,
      issue.jobIssueTypeName || "",
      issue.jobIssueTypeDescription || "",
      issue.responsibility || "",
      issue.issueStatus || "",
      issue.affectsServiceRate || "",
    ];
  });
};

export const invoiceDataFormat = (data: []) => {
  return data.map((customer: any) => {
    const invoicesCodeList: any[] = [];
    const invoicesList = customer.invoices.map((invoice: any) => {
      let stringifiedDate = "";
      if (invoice.invoiceDate) {
        const dateNumber = parseInt(invoice.invoiceDate);
        stringifiedDate = displayDateFormat(new Date(dateNumber));
      }
      invoicesCodeList.push({
        label: invoice.invoiceNumber,
        value: invoice.filePath,
      });
      return [
        invoice.invoiceNumber,
        invoice.invoiceType,
        stringifiedDate || "",
        invoice.totalAmount,
        invoice.creditedInvoiceNumber,
      ];
    });
    return {
      value: customer.id,
      label: customer.customer,
      invoices: invoicesList,
      invoicesCodes: invoicesCodeList,
    };
  });
};

export const invoiceChartDataFormat = (data: any[]): InvoiceGraphEntry[] => {
  return data.map((period: any) => {
    return {
      name: period.MMMyyyy,
      invoice: period.invoiceInclCreditNote,
      credit: period.creditNoteValue,
    };
  });
};

export const documentsDataFormat = (data: any[]) => {
  return data.map((doc: any) => {
    return [
      doc.site ? doc.site : "",
      doc.documentRef ? doc.documentRef : "",
      doc.fileName ? doc.fileName : "",
      doc.endOfValidity ? displayDateStringFormat(doc.endOfValidity) : "",
      doc.documentType ? doc.documentType : "",
      doc.filePath ? doc.filePath : "",
      doc.source ? doc.source : "",
      doc.fileType ? doc.fileType : "",
    ];
  });
};

export const auditsDataFormat = (data: any[]) => {
  return data.map((doc: any) => {
    return [
      doc.documentType ? doc.documentType : "",
      doc.customerName ? doc.customerName : "",
      doc.fileName ? doc.fileName : "",
      doc.fileType ? doc.fileType : "",
      doc.lastModifiedDate
        ? displayDateStringFormat(doc.lastModifiedDate)
        : "",
      doc.filePath ? doc.filePath : "",
    ];
  });
};

export const parseAchievementTippingData = (data: any[]) => {
  return data.map((item, idx) => {
    return {
      title: item.title,
      value: item.value,
      icon: achievementTippingDataIcons[idx],
    };
  });
};

export const parseLevelData = (data: any[]) => {
  return data.map((item, idx) => {
    return {
      name: item.level,
      value: item.tonnage,
      fill: levelColorCodes[idx % levelColorCodes.length],
    };
  });
};

export const parseTonnageData = (data: any[]) => {
  return data.map((item) => {
    return {
      name: item.periodLabel,
      value: item.totalWeight,
    };
  });
};

export const parseFeeData = (data: any[]) => {
  return data.map((item) => {
    return {
      name: item.periodLabel,
      fee: item.totalFee,
      rebate: item.totalRebate,
    };
  });
};

export const parseApiConnections = (data: any[]) => {
  const tempApiList: ApiConnectionDataEntry[] = [];
  const tempApiTable: any[][] = [];
  data.forEach((elem: any) => {
    tempApiList.push({
      id: elem.id,
      name: elem.attributes.name,
      application_id: elem.attributes.application_id || "",
      weightron_uid: elem.attributes.weightron_uid || "",
      weightron_secret: elem.attributes.weightron_secret || "",
      weightron_key: elem.attributes.weightron_key || "",
      endpoint_url: elem.attributes.endpoint_url || "",
    } as ApiConnectionDataEntry);
    const updatedAt = new Date(elem.attributes.updatedAt);
    const formattedDate = displayDateFormat(updatedAt);
    const formattedTime = updatedAt.toLocaleString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    tempApiTable.push([
      elem.attributes.name,
      elem.attributes.weightron_uid,
      elem.attributes.active,
      `${formattedDate} ${formattedTime}`,
      elem.id,
      elem.id,
      elem.id,
    ]);
  });
  return {
    apiList: tempApiList,
    apiTable: tempApiTable,
  };
};
